import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Tooltip,
} from "@mui/material";
import MDModal from "components/MDModal";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import MultiSelect from "components/SoftMultiSelect";
import { generateRandomCode } from "utils/generateRandomCode";
import { MarginTwoTone } from "@mui/icons-material";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";

export default function CreateCouponModal({ open, onClose, data, products, refetchCoupons, editMode = false, editData = null }) {
  const service = useAppServices();
  const [agency] = useAgencyInfo();
  const [loading, setLoading] = useState(false);

  // Initialize form with edit data if in edit mode
  const getInitialInputs = () => {
    if (editMode && editData) {
      const coupon = editData.coupon;
      return {
        name: coupon.name || "",
        code: editData.code || "",
        type: coupon.amount_off ? "Fixed Amount" : "Percentage",
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        max_redemptions: coupon.max_redemptions || "",
        max_redemptions_per_customer: "",
        products: coupon.applies_to?.products || [],
        duration: coupon.duration || "",
        duration_in_months: coupon.duration_in_months || "",
        amount: coupon.amount_off ? (coupon.amount_off / 100).toString() : "",
        percent: coupon.percent_off || "",
        currency: coupon.currency?.toUpperCase() || 'USD'
      };
    }
    return {
      name: data?.name || "",
      code: data?.code || "",
      type: data?.type || "",
      startDate: data?.startDate || "",
      startTime: data?.startTime || "",
      endDate: data?.endDate || "",
      endTime: data?.endTime || "",
      max_redemptions: data?.max_redemptions || "",
      max_redemptions_per_customer: data?.max_redemptions_per_customer || "",
      products: data?.products || [],
      duration: data?.duration || "",
      duration_in_months: data?.duration_in_months || "",
      amount: data?.amount || "",
      percent: data?.percent || "",
      currency: data?.currency || 'USD'
    };
  };

  const [inputs, setInputs] = useState(getInitialInputs());

  const getInitialCheckboxes = () => {
    if (editMode && editData) {
      const coupon = editData.coupon;
      return {
        limitRedemptions: !!coupon.max_redemptions,
        limitProducts: !!(coupon.applies_to?.products?.length),
        limitMaxRedemptionsPerUser: false,
        limitMonths: coupon.duration === "forever" || coupon.duration === "repeating",
      };
    }
    return {
      limitRedemptions: data?.max_redemptions || false,
      limitProducts: data?.products?.length || false,
      limitMaxRedemptionsPerUser: data?.max_redemptions_per_customer || false,
      limitMonths: data?.duration || false,
    };
  };

  const [checkboxeState, setCheckboxState] = useState(getInitialCheckboxes());

  // Update form when editData changes
  useEffect(() => {
    if (editMode && editData) {
      const newInputs = getInitialInputs();
      const newCheckboxes = getInitialCheckboxes();
      setInputs(newInputs);
      setCheckboxState(newCheckboxes);
    } else if (!editMode) {
      // Reset form when switching back to create mode
      setInputs(getInitialInputs());
      setCheckboxState(getInitialCheckboxes());
    }
  }, [editMode, editData]);

  const handleGenerateCode = () => {
    const code = generateRandomCode(10);
    setInputs((prev) => ({ ...prev, code }));
  };

  // const filteredProducts = products.filter((product) => product.isenable_account === true);

  const handleSubmit = async () => {
    setLoading(true);
    // Build a sanitized payload for backend/Stripe: cast numbers, omit empty strings
    const parsedPercent =
      inputs.type === "Percentage" && inputs.percent !== ""
        ? parseFloat(String(inputs.percent).toString().replace(/%/g, ""))
        : undefined;

    const parsedAmount =
      inputs.type === "Fixed Amount" && inputs.amount !== ""
        ? Number(inputs.amount)
        : undefined;

    const parsedMaxRedemptions =
      checkboxeState.limitRedemptions && inputs.max_redemptions !== ""
        ? parseInt(inputs.max_redemptions, 10)
        : undefined; // undefined => unlimited

    const parsedDurationInMonths =
      checkboxeState.limitMonths && inputs.duration === "repeat" && inputs.duration_in_months !== ""
        ? parseInt(inputs.duration_in_months, 10)
        : undefined;

    const includeProducts = checkboxeState.limitProducts ? inputs.products : undefined;

    // Base payload
    const rawPayload = {
      name: inputs.name || undefined,
      code: inputs.code || undefined,
      type: inputs.type || undefined,
      // dates/times: omit empty strings
      startDate: inputs.startDate || undefined,
      startTime: inputs.startTime || undefined,
      endDate: inputs.endDate || undefined,
      endTime: inputs.endTime || undefined,
      // limits
      max_redemptions: parsedMaxRedemptions, // omit when undefined => unlimited
      max_redemptions_per_customer: inputs.max_redemptions_per_customer || undefined,
      products: includeProducts && includeProducts.length ? includeProducts : undefined,
      duration: checkboxeState.limitMonths ? inputs.duration : undefined,
      duration_in_months: parsedDurationInMonths,
      redeem_by: (inputs.duration !== "forever" && inputs.endDate)
        ? Math.floor(new Date(`${inputs.endDate}T${inputs.endTime || "23:59"}:00`).getTime() / 1000)
        : undefined,
      // discount values
      amount: parsedAmount,
      percent: parsedPercent,
      agency_id: agency._id,
      currency: inputs.currency,
    };

    // Remove undefined/null/empty-string fields
    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(([_, v]) => v !== undefined && v !== null && v !== "")
    );

    let response, error;

    if (editMode) {
      // Update existing coupon
      // Note: Stripe only allows updating name and metadata after creation
      const updatePayload = {
        agency_id: agency._id,
        code: editData.code, // Use original code for identification
        name: inputs.name, // Only name can be updated in Stripe
      };
      ({ response, error } = await service.coupons.updateCoupon({ payload: updatePayload, toaster: true }));
    } else {
      // Create new coupon
      ({ response, error } = await service.coupons.createCoupon({ payload, toaster: true }));
    }

    if (response?.success) {
      refetchCoupons();
      onClose();
    }
    setLoading(false);
  };

  return (
    <MDModal open={open} onClose={onClose}>
      <Box className="coupon-modal">
        {/* Header */}
        <div className="coupon-header">
          <h2>{editMode ? "Edit Coupon" : "Create Coupon"}</h2>
          <p>Offer exciting discounts on one-time and subscription products</p>
        </div>

        {/* Form */}
        <div className="coupon-form">
          {/* Coupon Name */}
          <div>
            <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
              Coupon Name <span style={{ color: "red" }}>*</span>
            </span>
            <SoftInput
              fullWidth
              placeholder="Enter Coupon Name"
              required
              variant="outlined"
              size="small"
              style={{ padding: "8px" }}
              value={inputs.name}
              onChange={(e) => setInputs((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          {/* Coupon Code + Generate */}
          <div className="row" style={{ alignItems: "end" }}>
            <div style={{ width: "100%" }}>
              <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
                Coupon Code <span style={{ color: "red" }}>*</span>
              </span>
              <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                <div>
                  <SoftInput
                    placeholder="Enter Coupon Code"
                    style={{ padding: "8px" }}
                    required
                    variant="outlined"
                    size="small"
                    value={inputs.code}
                    onChange={(e) => setInputs((prev) => ({ ...prev, code: e.target.value }))}
                    disabled={editMode}
                  />
                </div>
              </Tooltip>
            </div>
            {!editMode && <button
              style={{
                background: "white",
                color: "black",
                border: "1px solid #d9d9d9",
                padding: "10px 20px",
                borderRadius: "5px",
                cursor: "pointer",
                height: "fit-content",
              }}
              onClick={handleGenerateCode}
            >
              Generate
            </button>}
          </div>

          {/* Coupon Type */}
          <div>
            <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
              Coupon Type <span style={{ color: "red" }}>*</span>
            </span>
            <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
              <div>
                <SoftSelect
                  value={inputs.type ? { value: inputs.type, label: inputs.type } : null}
                  options={["Percentage", "Fixed Amount"].map((entry) => ({
                    value: entry,
                    label: entry,
                  }))}
                  onChange={(e) => setInputs((prev) => ({ ...prev, type: e.value }))}
                  size="small"
                  isDisabled={editMode}
                />
              </div>
            </Tooltip>
            {inputs.type !== "" && (
              <div style={{ marginTop: "10px" }}>
                <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                  <SoftInput
                    placeholder={inputs.type === "Fixed Amount" ? "Enter Amount" : "Enter Percentage"}
                    style={{ padding: "8px", cursor: editMode ? "not-allowed" : "auto" }}
                    required
                    variant="outlined"
                    size="small"
                    value={inputs.type === "Fixed Amount" ? inputs.amount : inputs.percent}
                    onChange={(e) =>
                      setInputs((prev) => ({
                        ...prev,
                        [inputs.type === "Fixed Amount" ? "amount" : "percent"]: e.target.value,
                      }))
                    }
                    disabled={editMode}
                  />
                </Tooltip>
              </div>
            )}
          </div>
          {inputs.type === 'Fixed Amount' && <div>
            <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>Select Currency</span>
            <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
              <div>
                <SoftSelect
                  value={inputs.currency ? { value: inputs.currency, label: inputs.currency } : null}
                  options={["USD", "GBP"].map((entry) => ({
                    value: entry,
                    label: entry,
                  }))}
                  onChange={(e) => setInputs((prev) => ({ ...prev, currency: e.value }))}
                  size="small"
                  isDisabled={editMode}
                  styles={{
                    container: (base) => ({
                      ...base,
                      cursor: editMode ? "not-allowed" : "default",
                    }),
                    control: (base) => ({
                      ...base,
                      cursor: editMode ? "not-allowed" : "default",
                    }),
                  }}
                />
              </div>
            </Tooltip>
          </div>}

          {/* Start Date + Time */}
          <div className="row">
            <div style={{ width: "100%" }}>
              <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
                Start Date <span style={{ color: "red" }}>*</span>
              </span>
              <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                <div>
                  <SoftInput
                    type="date"
                    required
                    size="small"
                    style={{ padding: "10px 10px", cursor: editMode ? "not-allowed" : "auto" }}
                    value={inputs.startDate}
                    onChange={(e) => setInputs((prev) => ({ ...prev, startDate: e.target.value }))}
                    disabled={editMode}
                  />
                </div>
              </Tooltip>
            </div>
            <div style={{ width: "100%" }}>
              <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
                Start Time
              </span>
              <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                <div>
                  <SoftInput
                    type="time"
                    size="small"
                    style={{ padding: "10px 10px", cursor: editMode ? "not-allowed" : "auto" }}
                    value={inputs.startTime}
                    onChange={(e) => setInputs((prev) => ({ ...prev, startTime: e.target.value }))}
                    disabled={editMode}
                  />
                </div>
              </Tooltip>
            </div>
          </div>

          {/* End Date + Time */}
          <div className="row">
            <div style={{ width: "100%" }}>
              <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
                End Date
              </span>
              <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                <div>
                  <SoftInput
                    type="date"
                    size="small"
                    style={{ padding: "10px 10px", cursor: editMode ? "not-allowed" : "auto" }}
                    value={inputs.endDate}
                    onChange={(e) => setInputs((prev) => ({ ...prev, endDate: e.target.value }))}
                    disabled={editMode}
                  />
                </div>
              </Tooltip>
            </div>
            <div style={{ width: "100%" }}>
              <span style={{ marginBottom: 0, fontSize: "14px", fontWeight: "bold" }}>
                End Time
              </span>
              <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                <div>
                  <SoftInput
                    type="time"
                    size="small"
                    style={{ padding: "10px 10px", cursor: editMode ? "not-allowed" : "auto" }}
                    value={inputs.endTime}
                    onChange={(e) => setInputs((prev) => ({ ...prev, endTime: e.target.value }))}
                    disabled={editMode}
                  />
                </div>
              </Tooltip>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="checkbox-group" style={{ padding: "0px 10px" }}>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxeState.limitRedemptions}
                    onChange={(e) =>
                      setCheckboxState((prev) => ({ ...prev, limitRedemptions: e.target.checked }))
                    }
                    disabled={editMode}
                  />
                }
                label="Limit the total number of times the coupon can be redeemed"
                sx={{
                  color: "#000",
                  fontWeight: "medium",
                  cursor: editMode ? "not-allowed" : "pointer",
                  "& .MuiFormControlLabel-label": {
                    color: "#000",
                    fontWeight: "medium",
                    cursor: editMode ? "not-allowed" : "pointer",
                  },
                }}
              />
              {checkboxeState?.limitRedemptions && (
                <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                  <div style={{ cursor: editMode ? "not-allowed" : "auto" }}>
                    <SoftInput
                      type="number"
                      value={inputs.max_redemptions}
                      placeholder="Enter number of redemptions"
                      style={{ marginBlock: "8px", marginInline: "20px", cursor: editMode ? "not-allowed" : "auto" }}
                      onChange={(e) =>
                        setInputs((prev) => ({ ...prev, max_redemptions: e.target.value }))
                      }
                      disabled={editMode}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxeState.limitProducts}
                    onChange={(e) =>
                      setCheckboxState((prev) => ({ ...prev, limitProducts: e.target.checked }))
                    }
                    disabled={editMode}
                  />
                }
                label="Limit this coupon to selected products/offers"
                sx={{
                  color: "#000",
                  fontWeight: "medium",
                  cursor: editMode ? "not-allowed" : "pointer",
                  "& .MuiFormControlLabel-label": {
                    color: "#000",
                    fontWeight: "medium",
                    cursor: editMode ? "not-allowed" : "pointer",
                  },
                }}
              />
              {checkboxeState?.limitProducts && (
                <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                  <div style={{ marginBlock: "8px", marginInline: "20px", width: "100%", cursor: editMode ? "not-allowed" : "auto" }}>
                    <MultiSelect
                      value={inputs.products}
                      options={products?.map((product) => ({
                        label: product.product_name,
                        value: product.product_id,
                      }))}
                      onChange={(e) => setInputs((prev) => ({ ...prev, products: e.value }))}
                      isDisabled={editMode}
                      styles={{
                        container: (base) => ({
                          ...base,
                          cursor: editMode ? "not-allowed" : "default",
                        }),
                        control: (base) => ({
                          ...base,
                          cursor: editMode ? "not-allowed" : "default",
                        }),
                      }}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!inputs.max_redemptions_per_customer}
                    onChange={(e) => {
                      e.target.checked
                        ? setInputs((prev) => ({ ...prev, max_redemptions_per_customer: 1 }))
                        : setInputs((prev) => ({
                          ...prev,
                          max_redemptions_per_customer: undefined,
                        }));
                    }}
                    disabled={editMode}
                  />
                }
                label="Limit to one use per customer"
                sx={{
                  color: "#000",
                  fontWeight: "medium",
                  cursor: editMode ? "not-allowed" : "pointer",
                  "& .MuiFormControlLabel-label": {
                    color: "#000",
                    fontWeight: "medium",
                    cursor: editMode ? "not-allowed" : "pointer",
                  },
                }}
              />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkboxeState.limitMonths}
                  onChange={(e) =>
                    setCheckboxState((prev) => ({ ...prev, limitMonths: e.target.checked }))
                  }
                  disabled={editMode}
                />
              }
              label="Also apply to recurring/future payments based on duration or forever, if applicable"
              sx={{
                color: "#000",
                fontWeight: "medium",
                display: "flex",
                cursor: editMode ? "not-allowed" : "pointer",
                "& .MuiFormControlLabel-label": {
                  color: "#000",
                  fontWeight: "medium",
                  lineHeight: "20px",
                  cursor: editMode ? "not-allowed" : "pointer",
                },
              }}
            />
          </div>

          {/* Radio Options */}
          {checkboxeState.limitMonths && (
            <FormControl component="fieldset" sx={{ padding: "0px 30px" }}>
              <RadioGroup
                value={inputs.duration}
                onChange={(e) => setInputs((prev) => ({ ...prev, duration: e.target.value }))}
              >
                <FormControlLabel
                  value="forever"
                  control={<Radio />}
                  label="Forever"
                  disabled={editMode}
                  sx={{
                    color: "#000",
                    fontWeight: "medium",
                    cursor: editMode ? "not-allowed" : "pointer",
                    "& .MuiFormControlLabel-label": {
                      color: "#000",
                      fontWeight: "medium",
                      cursor: editMode ? "not-allowed" : "pointer",
                    },
                  }}
                />
                <FormControlLabel
                  value="repeat"
                  control={<Radio />}
                  label="Multiple Months"
                  disabled={editMode}
                  sx={{
                    color: "#000",
                    fontWeight: "medium",
                    "& .MuiFormControlLabel-label": {
                      color: "#000",
                      fontWeight: "medium",
                    },
                  }}
                />
              </RadioGroup>
              {inputs.duration !== "" && inputs.duration === "repeat" && (
                <Tooltip title={editMode ? "You cannot edit this field" : ""} followCursor>
                  <div style={{ cursor: editMode ? "not-allowed" : "auto" }}>
                    <SoftInput
                      type="number"
                      value={inputs.duration_in_months}
                      placeholder="Enter number of months"
                      style={{ marginBlock: "8px", marginInline: "20px", cursor: editMode ? "not-allowed" : "auto" }}
                      onChange={(e) =>
                        setInputs((prev) => ({
                          ...prev,
                          duration_in_months: e.target.value < 1 ? 1 : e.target.value,
                        }))
                      }
                      disabled={editMode}
                    />
                  </div>
                </Tooltip>
              )}
            </FormControl>
          )}

          {/* Note */}
          <p className="note">
            <strong>Note:</strong> For subscriptions, this determines for how long this coupon will
            apply once redeemed, irrespective of frequency. <a href="#">Learn More</a>
          </p>

          {/* Action Buttons */}
          <div className="actions">
            <button
              style={{
                background: "white",
                color: "black",
                border: "1px solid #d9d9d9",
                padding: "10px 20px",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              style={{
                background: "black",
                color: "white",
                padding: "10px 20px",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? "Wait..." : (editMode ? "Update" : "Create")}
            </button>
          </div>
        </div>
      </Box>
    </MDModal>
  );
}
